import React, { useMemo, useState } from "react";
import { Pagination } from "@pd/pd-ui-kit";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";

import Button from "~/shared/ui/Fields/Button";
import { UploadErrorRow, UploadResponseDto } from "~/entity/Uploads";
import { TableV2 } from "~/shared/ui/TableV2";

import s from "./RegMenCheckStep.less";

interface IProps {
  // PASDILER-1964: результат POST /uploads/upload-values (type=userToDealer)
  result: UploadResponseDto | null;
  loading?: boolean;
  onBack: () => void;
  onContinue: () => void;
}

const RegMenCheckStep: React.FC<IProps> = ({
  result,
  loading,
  onBack,
  onContinue,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(25);

  // Клиентская пагинация по errorRows. Ответ бэка возвращает весь массив
  // ошибок одним ответом — серверной пагинации нет. Перейти на неё, если объёмы
  // перестанут помещаться в один ответ.
  const rows = result?.errorRows ?? [];
  const total = rows.length;
  const pageRows = useMemo(() => {
    const start = page * count;
    // key = row+field. Одна строка файла может дать несколько ошибок
    // по разным полям → row не уникален. Добавить field (и индекс для подстраховки).
    return rows
      .slice(start, start + count)
      .map((r, i) => ({ ...r, key: `${r.row}_${r.field}_${i}` }));
  }, [rows, page, count]);

  const columns: ColumnsType<UploadErrorRow & { key: string }> = [
    { title: t("uploads.col_row"), dataIndex: "row", key: "row", width: 70 },
    { title: t("uploads.col_field"), dataIndex: "field", key: "field" },
    { title: t("uploads.col_value"), dataIndex: "value", key: "value" },
    { title: t("uploads.col_output"), dataIndex: "error", key: "error" },
  ];

  const hasOnlyErrors = result?.status === "ERROR";
  const statusColor =
    result?.status === "SUCCESS"
      ? "#1a7f37"
      : result?.status === "PARTIAL_SUCCESS"
        ? "#b08800"
        : "#cb242d";

  return (
    <div className={s.wrapper}>
      {result?.message && (
        <div className={s.summary} style={{ color: statusColor }}>
          <span className={s.summaryStatus}>
            {t(`uploads.status_${result.status}`)}
          </span>
          <span className={s.summaryMessage}>{result.message}</span>
        </div>
      )}
      <TableV2
        columns={columns}
        dataSource={pageRows}
        rowKey="key"
        loading={loading}
        scroll={{ x: "max-content" }}
      />
      {total > 0 && (
        <div className={s.footer}>
          <Pagination
            count={count}
            page={page + 1}
            totalCount={total}
            onChangeCount={(n: number) => {
              setCount(n || 25);
              setPage(0);
            }}
            onChangePage={(n: number) => setPage(Math.max(0, (n || 1) - 1))}
          />
        </div>
      )}
      <div className={s.buttons}>
        <Button txtBtn={t("uploads.another_file")} onClick={onBack} />
        <Button txtBtn={t("ui.back")} onClick={onBack} />
        {!hasOnlyErrors && (
          <Button txtBtn={t("uploads.continue")} orange onClick={onContinue} />
        )}
      </div>
    </div>
  );
};

export default RegMenCheckStep;
